import { rHandler } from '@/plugins/RHandler';
import { apiService } from '@/services/APIService';
import { objectsRHandler, DEFAULT_PARAMS } from '@/utils/objectsRHandler';

import Image from '@/models/list/document-constructor/image/index';
import Variable from '@/models/list/document-constructor/variable/index';
import Template from '@/modules/UIDesign/DocumentConstructor/models/template/index';
import Event from '@/models/list/event';
import { getEventVariables } from '@/modules/UIDesign/DocumentConstructor/helpers/eventVariables';

const TEMPLATES_API_PATH = 'event-certificate-templates';
const VARIABLES_API_PATH = `${TEMPLATES_API_PATH}/variables`;

export const DocumentConstructorManager = {
  templates: {
    ...objectsRHandler(TEMPLATES_API_PATH, Template),

    generatePDF({ args, data } = DEFAULT_PARAMS) {
      const [id] = args || [];
      return rHandler(
        apiService.post(`${TEMPLATES_API_PATH}/${id}/generate-pdf`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },
    check({ data } = DEFAULT_PARAMS) { // { event_id }
      return rHandler(
        apiService.post(`${TEMPLATES_API_PATH}/check-template`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },
  },
  variables: {
    ...objectsRHandler(VARIABLES_API_PATH, Variable),
    async forEvent({ event }) {
      const model = event instanceof Event ? event : new Event(event);
      const [catalog, attributes, nominations] = await Promise.all([
        DocumentConstructorManager.variables.index({ params: { event_id: model.id } }),
        model.getAttributes(),
        model.fetchNominations({ expand: 'groups' }),
      ]);
      if (catalog.error) return catalog;
      if (attributes.error) return attributes;
      if (!nominations?.data) return { error: nominations };
      return { ...catalog, items: getEventVariables(catalog.items, attributes.items, model) };
    },
  },
  plates: {
    list({ params } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.get('/certificate-settings/certificate-plates', params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Image(i, `/certificate-settings/certificate-plates`)),
        }),
        (error) => ({ error }),
      );
    },
    create({ data } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.post('/certificate-settings/certificate-plates', data),
        (response) => ({
          response,
          item: new Image(response.data.data, `/certificate-settings/certificate-plates`),
        }),
        (error) => ({ error }),
      );
    },
    delete({ data, args } = DEFAULT_PARAMS) {
      const [id] = args;

      return rHandler(
        apiService.delete(`/certificate-settings/certificate-plates/${id}`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },
  },
  images: {
    list({ params, args } = DEFAULT_PARAMS) {
      const [organizerId] = args;

      return rHandler(
        apiService.get(`/organizers/${organizerId}/logos`, params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Image(i, `/organizers/${organizerId}/logos`)),
        }),
        (error) => ({ error }),
      );
    },
    create({ data, args } = DEFAULT_PARAMS) {
      const [organizerId] = args;

      return rHandler(
        apiService.post(`/organizers/${organizerId}/logos/`, data),
        (response) => ({
          response,
          item: new Image(response.data.data, `/organizers/${organizerId}/logos`),
        }),
        (error) => ({ error }),
      );
    },
    update({ data, args } = DEFAULT_PARAMS) {
      const [organizerId, logoId] = args;

      return rHandler(
        apiService.put(`/organizers/${organizerId}/logos/${logoId}`, data),
        (response) => ({
          response,
          item: new Image(response.data.data, `/organizers/${organizerId}/logos/${logoId}`),
        }),
        (error) => ({ error }),
      );
    },
    delete({ data, args } = DEFAULT_PARAMS) {
      const [organizerId, logoId] = args;

      return rHandler(
        apiService.delete(`/organizers/${organizerId}/logos/${logoId}`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },
  },
};
